/* eslint @typescript-eslint/switch-exhaustiveness-check: "error" */
import React from 'react';
import type {
    Column,
    HBTableCellProps,
    DraggingContainerProps
} from './HBTable.types';
import HBTableCheckbox from './HBTableCheckbox';
import styled, { css } from 'styled-components';
import { truncatedText } from '../../styles/mixins';
import { vars } from '../../theme';
import {
    draggingContainerStyles,
    DraggingShadow,
    getColumnTransform
} from './HBTable.styles';
import { useIsColumnPinned, useSortableColumn } from './HBTableColumnHeader';

function HBTableCell<T>({ table, column, row }: HBTableCellProps<T>) {
    const { original } = column.columnDef.meta;
    const isPinnedColumn = useIsColumnPinned(table, column);

    const { setNodeRef, transform, transition, isDragging } = useSortableColumn(
        table,
        column
    );

    return (
        <TD
            className={column.id}
            $width={original.width}
            $isDragging={isDragging}
            $transition={transition}
            $isPinnedColumn={isPinnedColumn}
            ref={setNodeRef}
            style={{
                transform: getColumnTransform(transform, isPinnedColumn) // Transform changes rapidly and causes styled components performance issues
            }}
        >
            <DraggingShadow $isDragging={isDragging}></DraggingShadow>
            <Container $isDragging={isDragging}>
                <Checkbox table={table} column={column} row={row} />
                <InnerContainer $align={original.align}>
                    {original.render(row.original)}
                </InnerContainer>
            </Container>
        </TD>
    );
}

export default HBTableCell;

interface CheckboxProps<T> {
    table: HBTableCellProps<T>['table'];
    column: HBTableCellProps<T>['column'];
    row: HBTableCellProps<T>['row'];
}

function Checkbox<T>({ table, column, row }: CheckboxProps<T>) {
    const { helper } = table.options.meta;

    if (column.getIndex() !== 0 || !helper.hasRowSelection) {
        return null;
    }

    return (
        <HBTableCheckbox
            margin="0 8px 0 16px"
            checked={helper.isRowSelected(row)}
            testIdName="row"
            onChange={e =>
                helper.toggleTableRowSelection(row, e.target.checked)
            }
        />
    );
}

// -------------------------- Styles -------------------------- //

const TD = styled.td<
    {
        $width: Column<any>['width'];
    } & DraggingContainerProps
>`
    && {
        ${draggingContainerStyles}
        padding: 0;
        height: 100%;
        ${({ $width }) =>
            $width === 'fit'
                ? css`
                      width: 0;
                  `
                : css`
                      max-width: ${$width}px;
                      min-width: ${$width}px;
                  `}

        background-color: ${({ $isDragging }) =>
            $isDragging ? vars.colors.surface.item : 'none'};
    }
`;

const Container = styled.div<DraggingContainerProps>`
    display: flex;
    align-items: center;
    opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
`;

const InnerContainer = styled.div<{ $align?: 'left' | 'right' }>`
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: ${({ $align }) =>
        $align === 'right' ? 'safe flex-end' : 'flex-start'};
    width: 100%;
    > * {
        background-color: transparent;
        padding: 0;
        height: fit-content;
        min-height: initial;
        min-width: 0;
        ${truncatedText}
    }
`;
